export const DEFAULT_CANVAS_WIDTH = 720;
export const DEFAULT_CANVAS_HEIGHT = 1000;

// Match legacy Pixie export list — do NOT include textObj (runtime-only button↔text links)
export const FABRIC_CUSTOM_PROPS = [
  'selectable', 'name', 'senderNameChecked', 'useGoogleMap', 'selectedMapType',
  'useUserGPS', 'corners', 'vertical', 'horizontal', 'niceName', 'action',
  'iconfill', 'imagetype', 'zorder', 'url', 'uploadUrl', 'buttonSavedFill',
];

/** Break button/text/icon runtime links before Fabric JSON export */
function detachRuntimeLinks(fabricCanvas) {
  const restored = [];
  fabricCanvas.forEachObject((obj) => {
    if (obj.textObj) {
      restored.push({ obj, textObj: obj.textObj });
      delete obj.textObj;
    }
  });
  return restored;
}

function restoreRuntimeLinks(restored) {
  restored.forEach(({ obj, textObj }) => {
    obj.textObj = textObj;
  });
}

const TEXT_EXPORT_TYPES = new Set(['text', 'textbox', 'i-text']);

/** Fabric stylesToArray crashes if text.styles is undefined */
export function sanitizeObjectForFabricExport(obj) {
  if (!obj) return;

  if (TEXT_EXPORT_TYPES.has(obj.type)) {
    if (obj.styles == null || typeof obj.styles !== 'object') {
      obj.styles = {};
    }
    const lines = String(obj.text ?? '').split('\n');
    for (let i = 0; i < lines.length; i += 1) {
      if (obj.styles[i] == null || typeof obj.styles[i] !== 'object') {
        obj.styles[i] = {};
      }
    }
  }

  const children = typeof obj.getObjects === 'function' ? obj.getObjects() : obj._objects;
  if (children?.length) {
    children.forEach(sanitizeObjectForFabricExport);
  }
}

export function sanitizeCanvasForFabricExport(fabricCanvas) {
  fabricCanvas?.forEachObject?.(sanitizeObjectForFabricExport);
}

function exportCanvasJson(fabricCanvas) {
  sanitizeCanvasForFabricExport(fabricCanvas);
  try {
    return fabricCanvas.toDatalessJSON
      ? fabricCanvas.toDatalessJSON(FABRIC_CUSTOM_PROPS)
      : fabricCanvas.toJSON(FABRIC_CUSTOM_PROPS);
  } catch (err) {
    console.warn('toDatalessJSON failed, falling back to toJSON', err);
    return fabricCanvas.toJSON(FABRIC_CUSTOM_PROPS);
  }
}

export function parseDimensions(raw) {
  try {
    const dims = typeof raw === 'string' ? JSON.parse(raw) : raw;
    const width = Number(dims?.width) || 0;
    const height = Number(dims?.height) || 0;
    if (width < 50 || height < 50) {
      return { width: DEFAULT_CANVAS_WIDTH, height: DEFAULT_CANVAS_HEIGHT };
    }
    return { width, height };
  } catch {
    return { width: DEFAULT_CANVAS_WIDTH, height: DEFAULT_CANVAS_HEIGHT };
  }
}

export function color2rgba(color) {
  if (!color) return '0,0,0,1';
  const normalized = String(color).trim().toLowerCase();
  if (normalized === 'transparent') return '0,0,0,0';
  if (color.indexOf('#') === 0) {
    const h = color.replace('#', '');
    const result = [];
    if (h.length === 3) {
      for (let i = 0; i < h.length; i += 1) {
        result[i] = parseInt(h[i] + h[i], 16);
      }
    } else {
      for (let i = 0; i < 3; i += 1) {
        result[i] = parseInt(h[i * 2] + h[i * 2 + 1], 16);
      }
    }
    return `${result.join()},1`;
  }
  if (color.indexOf('rgba') === 0) {
    return color.replace('rgba(', '').replace(')', '').split(' ').join('');
  }
  if (color.indexOf('rgb') === 0) {
    const parts = color.replace('rgb(', '').replace(')', '').split(',').map((v) => v.trim());
    return `${parts.join()},1`;
  }
  return '0,0,0,1';
}

export function getEditableData(fabricCanvas, original) {
  const list = [];
  fabricCanvas.forEachObject((obj) => {
    if (obj?.name === 'editable' || obj?.name === 'entryfield') {
      list.push({
        zorder: obj.zorder || 0,
        top: obj.top / original.height,
        left: obj.left / original.width,
        height: obj.height / original.height,
        width: obj.width / original.width,
        type: obj.name,
      });
    }
  });
  return list;
}

export function getButtonData(fabricCanvas, original) {
  const list = [];
  fabricCanvas.forEachObject((button) => {
    if (
      button?.niceName &&
      (button.niceName.indexOf('button rect') === 0 ||
        button.niceName.indexOf('button circle') === 0 ||
        button.niceName.indexOf('button ellipse') === 0)
    ) {
      const data = (button.action || '').split(/:(.+)?/);
      const actionType = data[0] || '';
      const action = data[1] || '';
      const text = button.textObj;
      let icon = null;
      if (text?.textObj) icon = text.textObj.url;

      list.push({
        top: button.top / original.height,
        left: button.left / original.width,
        height: (button.height * (button.scaleY || 1)) / original.height,
        width: (button.width * (button.scaleX || 1)) / original.width,
        corner: button.rx || 0,
        icon,
        zorder: button.zorder || 0,
        style: button.name,
        text: text?.text || '',
        textColor: color2rgba(text?.fill),
        fontfamily: text?.fontFamily || 'Open Sans',
        fontsize: text?.fontSize || 24,
        color: color2rgba(button.fill),
        actionType,
        action,
      });
    }
  });
  return list;
}

export function getTextData(fabricCanvas, original) {
  const list = [];
  fabricCanvas.forEachObject((obj) => {
    if (
      obj.name === 'text' &&
      obj.niceName &&
      obj.niceName.indexOf('button') !== 0 &&
      obj.visible !== false
    ) {
      const angle = obj.angle || 0;
      obj.set({ angle: 0 }).setCoords();
      list.push({
        editable: obj.niceName === 'editable text',
        generated: obj.niceName === 'generated text',
        zorder: obj.zorder || 0,
        top: obj.top / original.height,
        left: obj.left / original.width,
        height: obj.height / original.height,
        width: obj.width / original.width,
        text: obj.text,
        fontfamily: obj.fontFamily,
        fontsize: obj.fontSize,
        color: color2rgba(obj.fill),
        angle,
        senderNameChecked: obj.senderNameChecked || false,
      });
      obj.set({ angle }).setCoords();
    }
  });
  return list;
}

export function getImageData(imageData, original) {
  const list = [];
  (imageData || []).forEach((image) => {
    const obj = image.obj;
    if (
      obj?.niceName &&
      (obj.niceName === 'image'
        || obj.niceName === 'sticker'
        || obj.name === 'sticker'
        || obj.niceName === 'shape') &&
      obj.name !== 'editable' &&
      obj.name !== 'entryfield'
    ) {
      const angle = obj.angle || 0;
      obj.set({ angle: 0 }).setCoords();
      const bounds = typeof obj.getBoundingRect === 'function'
        ? obj.getBoundingRect(true, true)
        : null;
      const objHeight = bounds?.height ?? (obj.height * (obj.scaleY || 1));
      const objWidth = bounds?.width ?? (obj.width * (obj.scaleX || 1));
      list.push({
        url: image.url,
        zorder: obj.zorder || 0,
        top: (bounds?.top ?? obj.top) / original.height,
        left: (bounds?.left ?? obj.left) / original.width,
        height: objHeight / original.height,
        width: objWidth / original.width,
        angle,
        useGoogleMap: obj.useGoogleMap || false,
        selectedMapType: obj.selectedMapType || null,
        useUserGPS: obj.useUserGPS || false,
      });
      obj.set({ angle }).setCoords();
    }
  });
  return list;
}

export function buildAppLayout(fabricCanvas, original, backgroundColor, previewImagePath, imageData = []) {
  const layoutJson = typeof backgroundColor === 'string' && backgroundColor.startsWith('{')
    ? JSON.parse(backgroundColor)
    : { background: backgroundColor };
  const bg = layoutJson.background || backgroundColor || '#FFFFFF';
  return {
    backgroundColor: color2rgba(bg),
    previewImage: previewImagePath,
    buttonLayout: getButtonData(fabricCanvas, original),
    textLayout: getTextData(fabricCanvas, original),
    imageLayout: getImageData(imageData, original),
    editableLayout: getEditableData(fabricCanvas, original),
  };
}

export function getDesignPayload(fabricCanvas, original, editorCustomActions = {}) {
  fabricCanvas.discardActiveObject();
  fabricCanvas.renderAll();

  const restored = detachRuntimeLinks(fabricCanvas);
  let fabricData;
  try {
    fabricData = exportCanvasJson(fabricCanvas);
  } finally {
    restoreRuntimeLinks(restored);
  }

  return {
    layout: JSON.stringify(fabricData),
    actions: JSON.stringify(editorCustomActions),
    dimensions: JSON.stringify({ width: original.width, height: original.height }),
  };
}

export function relinkButtonObjects(fabricCanvas) {
  let buttonCount = 0;
  fabricCanvas.forEachObject((obj) => {
    if (
      obj.niceName &&
      (obj.niceName.indexOf('button rect') === 0 ||
        obj.niceName.indexOf('button circle') === 0 ||
        obj.niceName.indexOf('button ellipse') === 0)
    ) {
      buttonCount += 1;
      const number = parseInt(
        obj.niceName.replace('button rect ', '').replace('button circle ', '').replace('button ellipse ', ''),
        10
      );
      fabricCanvas.forEachObject((text) => {
        if (text.niceName === `button text ${number}`) {
          obj.textObj = text;
          text.textObj = obj;
        }
      });
      if (obj.textObj) {
        fabricCanvas.forEachObject((icon) => {
          if (icon.niceName === `button icon ${number}`) {
            obj.textObj.textObj = icon;
            icon.textObj = obj;
          }
        });
      }
    }
  });
  return buttonCount;
}

/** Alias for plan/docs — same as shouldIncludeInImageUpload */
export function objectsForImageUpload(fabricCanvas) {
  const list = [];
  fabricCanvas?.forEachObject?.((obj) => {
    if (shouldIncludeInImageUpload(obj)) list.push(obj);
  });
  return list;
}

/** Match legacy TopPanelController.saveEditedImages object filter */
export function shouldIncludeInImageUpload(obj) {
  if (!obj || obj.visible === false) return false;
  if (obj.niceName === 'entryfield' || obj.niceName === 'editable') return false;
  if (
    obj.niceName === 'shape'
    || obj.niceName === 'image'
    || obj.niceName === 'sticker'
    || obj.name === 'sticker'
    || obj.name === 'icon'
  ) {
    return true;
  }
  return false;
}

export function isServerUploadUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return url.includes('/uploads/alert-templates') || url.includes('/uploads/');
}

/** Build imageData array for buildAppLayout from canvas objects */
export function collectImageData(fabricCanvas) {
  const data = [];
  fabricCanvas.forEachObject((obj) => {
    if (!obj) return;
    const isImageType =
      obj.type === 'image'
      || obj.type === 'group'
      || obj.name === 'sticker'
      || obj.niceName === 'image'
      || obj.name === 'image';
    if (!isImageType || obj.name === 'editable' || obj.name === 'entryfield') return;

    let url = obj.uploadUrl || obj.src || '';
    if (!url && typeof obj.getSrc === 'function') {
      try {
        url = obj.getSrc() || '';
      } catch {
        url = '';
      }
    }
    if (!url && obj.type === 'group') {
      url = obj.src || '';
    }
    if (url) {
      data.push({ obj, url });
    }
  });
  return data;
}

export function compareAppLayoutSnapshots(a, b) {
  const keys = ['backgroundColor', 'buttonLayout', 'textLayout', 'imageLayout', 'editableLayout'];
  const diffs = [];
  keys.forEach((key) => {
    const left = JSON.stringify(a?.[key] ?? null);
    const right = JSON.stringify(b?.[key] ?? null);
    if (left !== right) diffs.push(key);
  });
  return { equal: diffs.length === 0, diffs };
}
